/** The import review gate.
 *
 *  A multi-site workbook has one row per SITE, so the rows that land on
 *  one beneficiary can disagree — about a per-beneficiary VALUE (five
 *  different operation counts) or about who the beneficiary IS ("DE LA
 *  BRUYERE" and "ETA DU VIEUX PRE" arriving on one email). Both used to
 *  be settled silently by "last row wins" and reported afterwards, on a
 *  live campaign. Nothing is written until this screen comes back.
 *
 *  Two things make it usable rather than an endurance test:
 *
 *  - Value conflicts are decided per COLUMN, not per beneficiary. "300
 *    beneficiaries have a different operation count" is one question.
 *  - Identity conflicts the rules are confident about arrive already
 *    answered. Auto does NOT mean hidden: they are listed, collapsed,
 *    with their answer visible and one click from being changed.
 *
 *  For the real 2026-09-28 workbook that is 2 column questions and 16
 *  beneficiary questions, with 86 more shown for confirmation.
 *  See docs/IMPORT_CONFLICT_RESOLVER.md.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import type {
  ImportConflict,
  ImportConflictsResponse,
  ResolutionAction,
} from "@/lib/types";

type Props = {
  campaignId: string;
  jobId: string;
  onCancel: () => void;
  /** Fired once the job has been re-queued or sent back to mapping. */
  onResolved: (nextStatus: string) => void;
};

type Decision = { action: ResolutionAction; emails?: Record<string, string> };

export function ImportReviewModal({
  campaignId,
  jobId,
  onCancel,
  onResolved,
}: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  const [showAuto, setShowAuto] = useState(false);

  const conflicts = useQuery({
    queryKey: ["campaign-import-conflicts", campaignId, jobId],
    queryFn: async () => {
      const res = await api.get<ImportConflictsResponse>(
        ApiPaths.campaignImportConflicts(campaignId, jobId),
      );
      return res.data;
    },
  });

  const rows = useMemo(
    () => conflicts.data?.conflicts ?? [],
    [conflicts.data],
  );
  const columns = rows.filter((c) => c.scope === "column");
  const needsThought = rows.filter((c) => c.scope !== "column" && !c.auto);
  const preDecided = rows.filter((c) => c.scope !== "column" && c.auto);

  // A conflict is answered either because the operator picked
  // something, or because it arrived with a default we are willing to
  // stand behind. Column conflicts never arrive pre-decided — which of
  // five operation counts is "right" is not knowable from the file.
  const actionFor = (c: ImportConflict): ResolutionAction =>
    decisions[c.id]?.action ?? (c.auto ? c.default_action : c.default_action);
  const unanswered = [...columns, ...needsThought].filter(
    (c) => !decisions[c.id],
  );

  const submit = useMutation({
    mutationFn: async (remember: boolean) => {
      const payload: Record<string, unknown> = {};
      for (const c of rows) {
        const decided = decisions[c.id];
        const action = decided?.action ?? c.default_action;
        const entry: Record<string, unknown> = { action };
        if (action === "split" && decided?.emails) {
          entry.variants = Object.entries(decided.emails)
            .filter(([, email]) => email.trim())
            .map(([value, email]) => ({ value, email: email.trim() }));
        }
        payload[c.id] = entry;
      }
      const res = await api.post<{ status: string }>(
        ApiPaths.campaignImportConflicts(campaignId, jobId),
        { resolutions: payload, remember },
      );
      return res.data;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({
        queryKey: ["campaign-import-job", campaignId, jobId],
      });
      onResolved(data.status);
    },
  });

  function choose(id: string, action: ResolutionAction) {
    setDecisions((prev) => ({ ...prev, [id]: { ...prev[id], action } }));
  }

  function setEmail(id: string, value: string, email: string) {
    setDecisions((prev) => ({
      ...prev,
      [id]: {
        action: prev[id]?.action ?? "split",
        emails: { ...(prev[id]?.emails ?? {}), [value]: email },
      },
    }));
  }

  const summary = conflicts.data?.summary;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal-panel max-w-4xl">
        <header className="space-y-1">
          <h2 className="text-base font-semibold text-nb-text">
            {t("importReview.title")}
          </h2>
          <p className="text-xs text-nb-text-3">
            {t("importReview.subtitle")}
          </p>
          {summary && (
            <p className="text-xs text-nb-text-2" data-testid="review-summary">
              {t("importReview.summary", {
                decisions: summary.needs_decision,
                auto: summary.auto,
              })}
            </p>
          )}
        </header>

        {conflicts.isLoading && (
          <p className="py-6 text-sm text-nb-text-3">{t("common.loading")}</p>
        )}

        <div className="max-h-[60vh] space-y-5 overflow-y-auto pr-1">
          {columns.length > 0 && (
            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-nb-text-2">
                {t("importReview.columnSection")}
              </h3>
              {columns.map((c) => (
                <ColumnCard
                  key={c.id}
                  conflict={c}
                  action={actionFor(c)}
                  chosen={!!decisions[c.id]}
                  onChoose={(a) => choose(c.id, a)}
                />
              ))}
            </section>
          )}

          {needsThought.length > 0 && (
            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-nb-text-2">
                {t("importReview.decideSection", {
                  count: needsThought.length,
                })}
              </h3>
              {needsThought.map((c) => (
                <IdentityCard
                  key={c.id}
                  conflict={c}
                  action={actionFor(c)}
                  chosen={!!decisions[c.id]}
                  emails={decisions[c.id]?.emails ?? {}}
                  onChoose={(a) => choose(c.id, a)}
                  onEmail={(v, e) => setEmail(c.id, v, e)}
                />
              ))}
            </section>
          )}

          {preDecided.length > 0 && (
            <section className="space-y-2">
              <button
                type="button"
                className="flex w-full items-center justify-between text-left"
                onClick={() => setShowAuto((v) => !v)}
                aria-expanded={showAuto}
                data-testid="toggle-auto"
              >
                <h3 className="text-xs font-semibold uppercase tracking-wide text-nb-text-2">
                  {t("importReview.autoSection", {
                    count: preDecided.length,
                  })}
                </h3>
                <span className="text-xs text-nb-text-3">
                  {showAuto
                    ? t("importReview.hide")
                    : t("importReview.review")}
                </span>
              </button>
              <p className="text-[11px] text-nb-text-3">
                {t("importReview.autoHint")}
              </p>
              {showAuto &&
                preDecided.map((c) => (
                  <IdentityCard
                    key={c.id}
                    conflict={c}
                    action={actionFor(c)}
                    chosen={!!decisions[c.id]}
                    emails={decisions[c.id]?.emails ?? {}}
                    onChoose={(a) => choose(c.id, a)}
                    onEmail={(v, e) => setEmail(c.id, v, e)}
                  />
                ))}
            </section>
          )}
        </div>

        {submit.isError && (
          <p className="text-xs text-nb-danger">
            {t("importReview.submitFailed")}
          </p>
        )}

        <footer className="flex flex-wrap items-center justify-between gap-2 pt-2">
          <label className="flex items-center gap-2 text-xs text-nb-text-2">
            <input
              type="checkbox"
              defaultChecked
              id="remember-decisions"
              className="accent-nb-mint"
            />
            {t("importReview.remember")}
          </label>
          <div className="flex gap-2">
            <button type="button" className="btn-ghost" onClick={onCancel}>
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={submit.isPending}
              data-testid="apply-review"
              onClick={() => {
                const box = document.getElementById(
                  "remember-decisions",
                ) as HTMLInputElement | null;
                submit.mutate(box?.checked ?? true);
              }}
            >
              {unanswered.length > 0
                ? t("importReview.acceptDefaults", {
                    count: unanswered.length,
                  })
                : t("importReview.apply")}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function ActionPicker({
  conflict,
  action,
  onChoose,
}: {
  conflict: ImportConflict;
  action: ResolutionAction;
  onChoose: (a: ResolutionAction) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-1.5">
      {conflict.allowed_actions.map((a) => (
        <button
          key={a}
          type="button"
          onClick={() => onChoose(a)}
          aria-pressed={action === a}
          className={`rounded-full border px-2.5 py-1 text-[11px] transition ${
            action === a
              ? "border-nb-mint bg-nb-mint/10 text-nb-mint"
              : "border-nb-border text-nb-text-3 hover:text-nb-text-2"
          }`}
        >
          {t(`importReview.action.${a}`)}
        </button>
      ))}
    </div>
  );
}

function ColumnCard({
  conflict,
  action,
  chosen,
  onChoose,
}: {
  conflict: ImportConflict;
  action: ResolutionAction;
  chosen: boolean;
  onChoose: (a: ResolutionAction) => void;
}) {
  const { t } = useTranslation();
  return (
    <article
      className="rounded-lg border border-nb-border bg-nb-surface p-3 space-y-2"
      data-testid="column-conflict"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="text-sm font-medium text-nb-text">
          {conflict.column_label}
        </h4>
        <span className="text-[11px] text-nb-text-3">
          {t("importReview.affects", {
            count: conflict.beneficiaries_affected,
          })}
        </span>
      </div>
      <p className="text-xs text-nb-text-3">{t("importReview.valueHint")}</p>
      <ul className="space-y-0.5">
        {conflict.examples.slice(0, 3).map((ex) => (
          <li key={ex.channel} className="text-[11px] text-nb-text-2">
            <span className="text-nb-text-3">
              {ex.channel.replace(/^email:|^phone:/, "")}
            </span>{" "}
            <span className="tabular-nums">{ex.values.join(" · ")}</span>
          </li>
        ))}
      </ul>
      <ActionPicker
        conflict={conflict}
        action={action}
        onChoose={onChoose}
      />
      {!chosen && (
        <p className="text-[11px] text-nb-text-3">
          {t("importReview.defaultNote", {
            action: t(`importReview.action.${conflict.default_action}`),
          })}
        </p>
      )}
    </article>
  );
}

function IdentityCard({
  conflict,
  action,
  chosen,
  emails,
  onChoose,
  onEmail,
}: {
  conflict: ImportConflict;
  action: ResolutionAction;
  chosen: boolean;
  emails: Record<string, string>;
  onChoose: (a: ResolutionAction) => void;
  onEmail: (value: string, email: string) => void;
}) {
  const { t } = useTranslation();
  const contact = conflict.channel.replace(/^email:|^phone:/, "");
  // Distinct towns are the strongest available hint that a group is
  // really two companies — and NOT proof, which is why it's shown next
  // to the choice rather than acted on (docs §5 measured it at ~70%).
  const towns = new Set(
    conflict.variants.flatMap((v) => v.addresses).filter(Boolean),
  );

  return (
    <article
      className="rounded-lg border border-nb-border bg-nb-surface p-3 space-y-2"
      data-testid="identity-conflict"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="text-sm font-medium text-nb-text">{contact}</h4>
        {conflict.verdict && (
          <span className="rounded-full border border-nb-border px-2 py-0.5 text-[10px] uppercase tracking-wide text-nb-text-3">
            {t(`importReview.verdict.${conflict.verdict}`)}
          </span>
        )}
      </div>

      <ul className="space-y-1">
        {conflict.variants.map((v) => (
          <li key={v.value} className="text-xs">
            <span className="text-nb-text">{v.value}</span>
            {v.addresses[0] && (
              <span className="text-nb-text-3"> · {v.addresses[0]}</span>
            )}
            {action === "split" && conflict.split_needs_new_email && (
              <input
                type="email"
                className="input mt-1 w-full text-xs"
                placeholder={t("importReview.newEmailPlaceholder")}
                value={emails[v.value] ?? ""}
                onChange={(e) => onEmail(v.value, e.target.value)}
                aria-label={t("importReview.newEmailFor", { name: v.value })}
              />
            )}
          </li>
        ))}
      </ul>

      {towns.size > 1 && (
        <p className="text-[11px] text-nb-text-3">
          {t("importReview.differentPlaces")}
        </p>
      )}

      <ActionPicker
        conflict={conflict}
        action={action}
        onChoose={onChoose}
      />

      {action === "split" && conflict.split_needs_new_email && (
        <p className="text-[11px] text-nb-text-3">
          {t("importReview.splitEmailNote")}
        </p>
      )}
      {conflict.split_blocked_reason && (
        <p className="text-[11px] text-nb-danger">
          {conflict.split_blocked_reason}
        </p>
      )}
      {!chosen && conflict.auto && (
        <p className="text-[11px] text-nb-text-3">
          {t("importReview.autoNote", {
            action: t(`importReview.action.${conflict.default_action}`),
          })}
        </p>
      )}
    </article>
  );
}
